'use client'

import { Modal, ModalBody, ModalContent, ModalHeader, useDisclosure } from '@nextui-org/react'
import { addHours } from 'date-fns'
import { useTranslations } from 'next-intl'
import { useSearchParams } from 'next/navigation'
import { useCallback } from 'react'
import { useEffectOnce } from 'react-use'

import { BookingFormComponent } from '@/components/forms/booking'
import { MaintenanceModal, useMaintenance } from '@/components/ui/maintenance'
import { cookies } from '@/libs/cookie'
import { stripHtml } from '@/libs/utils'

interface ButtonBookingProps {
  branchId?: string
  allowChange?: boolean
}

export function ButtonBooking({ branchId }: ButtonBookingProps) {
  // __STATE's
  const t = useTranslations()
  const searchParams = useSearchParams()

  const { isOpen, onOpen, onOpenChange } = useDisclosure()
  const maintenance = useMaintenance()

  // __FUNCTION's
  const openBooking = useCallback(async () => {
    // Blocks instead of opening: the form posts to the booking API that maintenance takes offline.
    if (await maintenance.check()) return

    onOpen()
  }, [maintenance.check, onOpen])

  // __EFFECT's
  useEffectOnce(() => {
    const source = searchParams.get('utm_source')
    if (source && !cookies.get('source')) {
      cookies.set('source', source, {
        domain: process.env.NEXT_PUBLIC_COOKIE_DOMAIN,
        expires: addHours(new Date(), process.env.NEXT_PUBLIC_COOKIE_HOURS ? parseInt(process.env.NEXT_PUBLIC_COOKIE_HOURS) : 24),
        path: '/',
      })
    }

    const utmContent = searchParams.get('utm_content')
    if (utmContent && !cookies.get('utm_content')) {
      cookies.set('utm_content', Array.from(utmContent).slice(0, 10).join(''), {
        domain: process.env.NEXT_PUBLIC_COOKIE_DOMAIN,
        expires: addHours(new Date(), process.env.NEXT_PUBLIC_COOKIE_HOURS ? parseInt(process.env.NEXT_PUBLIC_COOKIE_HOURS) : 24),
        path: '/',
      })
    }
  })

  // __RENDER
  return (
    <>
      <button
        className='btn btn-primary mx-auto h-10 px-8 md:h-12'
        type='button'
        aria-label='Button Booknow'
        onClick={openBooking}>
        <span
          className='text z-10 text-2xl font-light uppercase leading-none tracking-widest'
          dangerouslySetInnerHTML={{ __html: stripHtml(t.raw('Menu.BOOKNOW_BUTTON')) }}
        />
      </button>

      <Modal isOpen={isOpen} onOpenChange={onOpenChange} size='md' radius='sm' placement='auto'>
        <ModalContent className='sm:max-w-2xl md:max-w-6xl'>
          <ModalHeader className='px-8 pt-8'>Online Booking</ModalHeader>

          <ModalBody className='gap-6 px-8 pb-8 pt-0 sm:grid'>
            <BookingFormComponent
              key='.form-online-booking'
              defaultBranchId={branchId}
              allowChange={true}
            />
          </ModalBody>
        </ModalContent>
      </Modal>

      <MaintenanceModal
        isOpen={maintenance.isBlocked}
        message={maintenance.message}
        onClose={maintenance.close}
      />
    </>
  )
}
